// Existing screens opt into v2 reads. Configuration, reservations and AI creation stay in their own APIs.
import { postV2 } from '@/api/heatmap-v2'
import { heatmapV2Mode } from './flag'
import { toV2Request, toV2Filters, unsupportedReason } from './request'
import { servingRange } from './watermark'

function fallback (legacy, outcome, reason) {
  outcome({ reason })
  return legacy()
}

async function selectRead (path, body, legacy, project = value => value, outcome = () => {}) {
  if (!heatmapV2Mode()) return fallback(legacy, outcome, 'disabled')
  try {
    return project(await postV2(path, body))
  } catch (error) {
    // Never bypass v2 resource authorization through the less restrictive old route.
    if (error.status === 401 || error.status === 403) throw error
    // eslint-disable-next-line no-console
    console.warn('[heatmap-v2] follow-up uses existing API:', path, error.status, error.code || error.message)
    return fallback(legacy, outcome, error.code || 'unavailable')
  }
}

export async function readPath (params, timezone, legacy, outcome = () => {}) {
  if (!heatmapV2Mode()) return fallback(legacy, outcome, 'disabled')
  const req = toV2Request(params, timezone)
  if (!req.supported) return fallback(legacy, outcome, req.reason)
  const range = await servingRange(params.aid, req.body.range)
  if (!range.ok) return fallback(legacy, outcome, range.reason)
  const body = { ...req.body, range: range.range, core: params.core === true || params.core === 'true' }
  if (params.conversion !== undefined && params.conversion !== null) {
    body.conversion = params.conversion === true || params.conversion === 'true'
  }
  return selectRead('path/screen', body, legacy, result => {
    outcome({ clippedAt: range.clipped ? range.range.endAt : null })
    return result
  }, outcome)
}

export async function readAnalytics (params, timezone, legacy, outcome = () => {}) {
  if (!heatmapV2Mode()) return fallback(legacy, outcome, 'disabled')
  const req = toV2Request(params, timezone)
  if (!req.supported) return fallback(legacy, outcome, req.reason)
  const range = await servingRange(params.aid, req.body.range)
  if (!range.ok) return fallback(legacy, outcome, range.reason)
  const { aid, filters } = req.body
  if (params.device) filters.devices = [params.device]
  return selectRead('analytics/screen', { aid, filters, range: range.range }, legacy, result => {
    outcome({ clippedAt: range.clipped ? range.range.endAt : null })
    return result
  }, outcome)
}

export async function readSwipe (params, timezone, legacy, outcome = () => {}) {
  if (!heatmapV2Mode()) return fallback(legacy, outcome, 'disabled')
  const req = toV2Request(params, timezone)
  if (!req.supported) return fallback(legacy, outcome, req.reason)
  const range = await servingRange(params.aid, req.body.range)
  if (!range.ok) return fallback(legacy, outcome, range.reason)
  return selectRead('swipe', { ...req.body, range: range.range }, legacy, result => {
    outcome({ clippedAt: range.clipped ? range.range.endAt : null })
    return result
  }, outcome)
}

// Timemachine reservations already contain UTC instants. Do not turn these into day ranges or clip playback.
export function readTimemachine (params, utcRange, legacy) {
  if (!utcRange) return legacy()
  if (unsupportedReason(params)) return legacy()
  return selectRead('timemachine/screen', {
    aid: params.aid,
    device: params.device,
    range: utcRange,
    filters: toV2Filters(params),
    intervalSec: Number(params.interval || 1)
  }, legacy)
}

// Resource dates are interpreted by the server using the authorized Funnel domain / AB Apply timezone.
export function readFunnelReport (params, legacy) {
  const { fid, device, startDate, endDate } = params
  return selectRead('funnel/report/screen', { fid, device, startDate, endDate }, legacy)
}

export function readFunnelStream (params, legacy) {
  const { fid, seq, startDate, endDate, order, page, itemNum } = params
  const filters = {}
  for (const key of ['dropped', 'cvr', 'sessionId', 'country', 'device', 'os', 'browser', 'sessionGteTime', 'sessionLteTime']) {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') filters[key] = params[key]
  }
  return selectRead('funnel/stream/screen', {
    fid, seq: Number(seq), startDate, endDate, order, page: Number(page), itemNum: Number(itemNum), filters
  }, legacy)
}

export function readABTarget (params, legacy) {
  const { abid, device, startDate, endDate, existing } = params
  return selectRead('ab/target/screen', { abid, device, startDate, endDate, existing }, legacy, result => result.targets)
}

// CTA and frequency panels use Apply-local dates, like the heatmap itself.
async function readCohortPanel (path, params, timezone, legacy, outcome) {
  if (!heatmapV2Mode()) return fallback(legacy, outcome, 'disabled')
  const req = toV2Request(params, timezone)
  if (!req.supported) return fallback(legacy, outcome, req.reason)
  const range = await servingRange(params.aid, req.body.range)
  if (!range.ok) return fallback(legacy, outcome, range.reason)
  const { aid, filters } = req.body
  if (params.device && path !== 'scroll/daily') filters.devices = [params.device]
  const body = path === 'scroll/daily' ? { ...req.body, range: range.range } : { aid, filters, range: range.range }
  if (path === 'frequency') {
    delete filters.cohort
    body.mode = params.freq_option === 'session' ? 'session' : 'pv'
  }
  return selectRead(path, body, legacy, result => {
    outcome({ clippedAt: range.clipped ? range.range.endAt : null })
    return result
  }, outcome)
}

export function readCta (params, timezone, legacy, outcome = () => {}) {
  return readCohortPanel('cta/screen', params, timezone, legacy, outcome)
}

export function readFrequency (params, timezone, legacy, outcome = () => {}) {
  return readCohortPanel('frequency', params, timezone, legacy, outcome)
}

export function readDailyDevice (params, timezone, legacy, outcome = () => {}) {
  return readCohortPanel('device/daily', params, timezone, legacy, outcome)
}

export function readUtm (params, timezone, legacy, outcome = () => {}) {
  return readCohortPanel('utm', params, timezone, legacy, outcome)
}

export function readDailyScroll (params, timezone, legacy, outcome = () => {}) {
  return readCohortPanel('scroll/daily', params, timezone, legacy, outcome)
}
